import type { Workspace } from '@molt/shared';
import { CopyPlus, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';

import { Button, Dialog, EmptyState, IconButton, Notice, TextField } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * FE-80.1 board-templates — the create-board-from-template dialog.
 *
 * ⚠ `VISIBILITY_CHOICES` is a MODULE constant, read inside a `.map()` in
 * the render. A module constant cannot call `t()`, so its arms hold
 * catalogue KEYS (`labelKey` / `descriptionKey`) and the render resolves
 * them — `workspace-switcher`'s ruling. The constant is not exported and
 * nothing outside this file reads it, so no consumer call site changes
 * with it.
 *
 * ⚠ `t` is destructured ABOVE the `if (!open) return null` guard: it is a
 * hook call and cannot sit after an early return.
 *
 * ⚠ `boardTemplates.create.copiedCount` is a plural and the number is
 * passed TWICE — once into the `{count}` slot and once as the third
 * argument that selects the form. Either half alone is wrong: without the
 * values object `interfaceMessage` throws, and without the third argument
 * `count` defaults to `0`, which renders `Copied 1 cards.` at arity one.
 */

export interface CreateFromTemplateDialogSubmit {
  readonly workspaceId: string;
  readonly visibility: 'private' | 'workspace_visible' | 'public';
  readonly keepCards: boolean;
  readonly title: string;
}

export interface CreateFromTemplateDialogProps {
  readonly open: boolean;
  readonly templateTitle: string;
  readonly workspaces: readonly Workspace[];
  readonly defaultWorkspaceId: string;
  readonly loading?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly cardsCopied?: number | undefined;
  readonly createdBoardPath?: string | undefined;
  readonly onCancel: () => void;
  readonly onCreate: (input: CreateFromTemplateDialogSubmit) => void;
}

export function CreateFromTemplateDialog({
  open,
  templateTitle,
  workspaces,
  defaultWorkspaceId,
  loading,
  busy,
  cardsCopied,
  createdBoardPath,
  onCancel,
  onCreate,
}: CreateFromTemplateDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const titleField = useRef<HTMLInputElement | null>(null);
  const formId = useId();
  const workspaceSelectId = useId();
  const visibilityLegendId = useId();
  const cleanTemplateTitle =
    templateTitle.trim() === ''
      ? t('boardTemplates.create.untitledTemplate')
      : templateTitle.trim();

  const [title, setTitle] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [visibility, setVisibility] =
    useState<CreateFromTemplateDialogSubmit['visibility']>('workspace_visible');
  const [keepCards, setKeepCards] = useState(true);
  const [titleTouched, setTitleTouched] = useState(false);

  const defaultDestinationWorkspaceId = useMemo(() => {
    if (workspaces.some((workspace) => workspace.id === defaultWorkspaceId)) {
      return defaultWorkspaceId;
    }
    return workspaces[0]?.id ?? '';
  }, [defaultWorkspaceId, workspaces]);

  const selectedWorkspaceId = workspaces.some((workspace) => workspace.id === workspaceId)
    ? workspaceId
    : defaultDestinationWorkspaceId;

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setWorkspaceId(defaultDestinationWorkspaceId);
    setVisibility('workspace_visible');
    setKeepCards(true);
    setTitleTouched(false);
  }, [defaultDestinationWorkspaceId, open]);

  if (!open) return null;

  const titleError =
    title.trim().length === 0 ? t('boardTemplates.create.titleRequired') : null;
  const visibleTitleError = titleTouched ? titleError : null;
  const hasWorkspaces = workspaces.length > 0;
  const controlsDisabled = busy === true || loading === true || !hasWorkspaces;
  const confirmDisabled =
    controlsDisabled || selectedWorkspaceId === '' || titleError !== null;
  const disabledReason =
    loading === true
      ? t('boardTemplates.create.loadingReason')
      : !hasWorkspaces
        ? t('boardTemplates.create.noWorkspacesReason')
        : (titleError ?? undefined);
  const disabledReasonProps = disabledReason ? { disabledReason } : {};
  const busyProps = busy === undefined ? {} : { busy };
  const resultVisible = cardsCopied !== undefined || createdBoardPath !== undefined;

  function submitCreate() {
    setTitleTouched(true);
    if (confirmDisabled) return;
    onCreate({
      workspaceId: selectedWorkspaceId,
      visibility,
      keepCards,
      title: title.trim(),
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('boardTemplates.create.title', { template: cleanTemplateTitle })}
      description={t('boardTemplates.create.description')}
      initialFocus={titleField}
      closeControl={
        <IconButton label={t('boardTemplates.create.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {resultVisible
              ? t('boardTemplates.create.done')
              : t('boardTemplates.create.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconStart={<CopyPlus aria-hidden="true" className="size-4" />}
            busyLabel={t('boardTemplates.create.busy')}
            disabled={confirmDisabled}
            type="submit"
            form={formId}
            {...busyProps}
            {...disabledReasonProps}
          >
            {t('boardTemplates.create.submit')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submitCreate();
        }}
      >
        {resultVisible ? (
          <Notice
            tone="info"
            title={t('boardTemplates.create.resultTitle')}
            body={
              cardsCopied === undefined
                ? t('boardTemplates.create.resultBody')
                : t(
                    'boardTemplates.create.copiedCount',
                    { count: cardsCopied },
                    cardsCopied,
                  )
            }
            action={
              createdBoardPath === undefined ? undefined : (
                <Button as="a" size="sm" variant="secondary" href={createdBoardPath}>
                  {t('boardTemplates.create.openCreated')}
                </Button>
              )
            }
          />
        ) : null}

        <TextField
          ref={titleField}
          label={t('boardTemplates.create.titleLabel')}
          value={title}
          disabled={controlsDisabled}
          onBlur={() => setTitleTouched(true)}
          onChange={(event) => {
            setTitleTouched(true);
            setTitle(event.currentTarget.value);
          }}
          {...(visibleTitleError === null ? {} : { error: visibleTitleError })}
        />

        {hasWorkspaces ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={workspaceSelectId} className="text-sm font-medium text-ink">
              {t('boardTemplates.create.workspaceLabel')}
            </label>
            <select
              id={workspaceSelectId}
              value={selectedWorkspaceId}
              disabled={controlsDisabled}
              onChange={(event) => setWorkspaceId(event.currentTarget.value)}
              className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent disabled:cursor-not-allowed disabled:text-ink-muted"
            >
              {workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name.trim() === ''
                    ? t('boardTemplates.create.untitledWorkspace')
                    : workspace.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('boardTemplates.create.noWorkspacesTitle')}
            body={t('boardTemplates.create.noWorkspacesReason')}
          />
        )}

        <fieldset
          className="flex flex-col gap-2"
          aria-labelledby={visibilityLegendId}
          disabled={controlsDisabled}
        >
          <legend id={visibilityLegendId} className="text-sm font-medium text-ink">
            {t('boardTemplates.create.visibilityLegend')}
          </legend>
          <div className="flex flex-col gap-1">
            {VISIBILITY_CHOICES.map((choice) => (
              <label
                key={choice.value}
                className="flex items-start gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-ink"
              >
                <input
                  type="radio"
                  name="create-from-template-visibility"
                  value={choice.value}
                  aria-label={t(choice.labelKey)}
                  checked={visibility === choice.value}
                  onChange={() => setVisibility(choice.value)}
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span>{t(choice.labelKey)}</span>
                  <span className="text-xs text-ink-muted">
                    {t(choice.descriptionKey)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex items-start gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-ink">
          <input
            type="checkbox"
            aria-label={t('boardTemplates.create.keepCards')}
            checked={keepCards}
            disabled={controlsDisabled}
            onChange={(event) => setKeepCards(event.currentTarget.checked)}
          />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span>{t('boardTemplates.create.keepCards')}</span>
            <span className="text-xs text-ink-muted">
              {t('boardTemplates.create.keepCardsDescription')}
            </span>
          </span>
        </label>
      </form>
    </Dialog>
  );
}

type TemplateVisibility = CreateFromTemplateDialogSubmit['visibility'];

const VISIBILITY_CHOICES: readonly {
  readonly value: TemplateVisibility;
  readonly labelKey: string;
  readonly descriptionKey: string;
}[] = [
  {
    value: 'private',
    labelKey: 'boardTemplates.create.visibility.private',
    descriptionKey: 'boardTemplates.create.visibility.privateDescription',
  },
  {
    value: 'workspace_visible',
    labelKey: 'boardTemplates.create.visibility.workspace',
    descriptionKey: 'boardTemplates.create.visibility.workspaceDescription',
  },
  {
    value: 'public',
    labelKey: 'boardTemplates.create.visibility.public',
    descriptionKey: 'boardTemplates.create.visibility.publicDescription',
  },
];
